import React from 'react';
import { useLazyQuery } from 'apollo-client-ppc';
import Button, { LoadingButton } from '@atlaskit/button';
import { getCustomerContactsBySite } from '@atlassian/temp-proxies';
import { isJiraProduct } from '@atlassian/cssxp-utils-product';
import { SourceSystem } from '@atlassian/cssxp-typescommon';
import { ErrorMessage, Field } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import SectionMessage from '@atlaskit/section-message';
import EditorSuccessIcon from '@atlaskit/icon/glyph/editor/success';
import EditorErrorIcon from '@atlaskit/icon/glyph/editor/error';
import { G500, R500 } from '@atlaskit/theme/colors';
import { Checkbox } from '@atlaskit/checkbox';
import { NotificationTypes, errorNotificationBuilder, useFeatureToggle, useNotification } from '@css-xp/base';
import { HelpContent, failureMessage } from '@css-xp/ppc-common';
import { usePartnerCatalogueContext } from './context/partner-catalogue-context';
import { fetchProductsUnderPPCloudSite } from './queries/sxp-queries/fetch-products-under-PPC-cloud-site';
import { ExistingProduct, PartnerQuoteLineItems, SiteDetails } from './types';
import { useValidateCloudSite } from './hooks/use-validate-cloud-site';
import { useExistingSiteProvisioning } from './hooks/use-existing-site-provisioning';
import './add-existing-site.scss';

export type AddExistingSiteProps = {
  transactionAccountId: string;
  lineItems: PartnerQuoteLineItems;
  // eslint-disable-next-line no-unused-vars
  onExistingSiteAdded: (params: {
    siteDetails: SiteDetails | undefined;
    existingInvoiceGroupId: string | undefined;
    isExistingSiteValidForProvisioning?: boolean;
    errorMessage?: string;
  }) => void;
};

const renderErrorMessage = (message: string) => {
  return (
    <>
      {`${message} `}
      {<HelpContent />}
    </>
  );
};

export const AddExistingSite: React.FC<AddExistingSiteProps> = ({
  transactionAccountId,
  onExistingSiteAdded,
  lineItems,
}) => {
  const {
    setFamilyContainerId,
    setInvoiceGroupIdAttachedToExistingSite,
    setExistingCloudSite,
    updateSiteCustomerContacts,
  } = usePartnerCatalogueContext();
  const [isAddingExistingSiteOptionSelected, setIsAddingExistingSiteOptionSelected] = React.useState(false);

  const [siteUrl, setSiteUrl] = React.useState('');
  const [areProductsFound, setAreProductsFound] = React.useState(false);
  const [errorMessages, setErrorMessages] = React.useState<string[]>([]);

  const { checkSiteUrl, isSiteValid } = useValidateCloudSite();
  const { validateSiteForProvisioning } = useExistingSiteProvisioning();

  const [isEnabled] = useFeatureToggle();

  const { notificationSent, setNotificationSent, sendErrorNotification } = useNotification();

  const [fetchAllProducts, { loading: isExistingInstanceBeingSearched }] = useLazyQuery<
    {
      productsUnderPPCCloudSite: ExistingProduct[];
    },
    { transactionAccountId: string; cloudSiteUrl: string }
  >(fetchProductsUnderPPCloudSite);

  const finalSiteUrl = siteUrl.trim();

  const fetchSiteCustomerContacts = React.useCallback(
    async (entitlementIds) => {
      try {
        updateSiteCustomerContacts({ loading: true, data: [] });
        const customerContacts = await getCustomerContactsBySite({
          entitlementIds,
          transactionAccountId,
        });
        updateSiteCustomerContacts({ loading: false, data: customerContacts });
      } catch (err) {
        if (!notificationSent) {
          const notificationOptions = errorNotificationBuilder()
            .isVersion(NotificationTypes.FLAG)
            .withHeader('Failed to fetch Customer Contacts')
            .withMessage(failureMessage())
            .withDismissAfter(5000)
            .build();
          sendErrorNotification(notificationOptions);
          setNotificationSent(true);
        }
        updateSiteCustomerContacts({ loading: false, data: [] });
      }
    },
    [notificationSent, sendErrorNotification, setNotificationSent, transactionAccountId, updateSiteCustomerContacts]
  );

  const fetchProducts = React.useCallback(async () => {
    const variables = {
      cloudSiteUrl: finalSiteUrl,
      transactionAccountId,
    };
    const { data } = await fetchAllProducts({
      variables,
    });

    const errorMessages: string[] = [];

    let productsUnderPPCCloudSite = data?.productsUnderPPCCloudSite ?? [];

    //Filtering out atlassian cloud product which is common in all cloud site url using its productCatalogId
    productsUnderPPCCloudSite = productsUnderPPCCloudSite?.filter(
      (product) => product.productCatalogId !== 'atlassian.cloud'
    );

    const jiraProducts = productsUnderPPCCloudSite.filter((product) => isJiraProduct(product.productCatalogId));
    const familyContainerId = jiraProducts?.[0]?.relatesFromEntitlements?.[0].entitlementId;
    setFamilyContainerId(familyContainerId);

    const noMatchingProductsFound = productsUnderPPCCloudSite.length === 0;
    if (noMatchingProductsFound) {
      errorMessages.push('Select a site with existing subscriptions under your billing account.');
    }
    const existingInvoiceGroupId = productsUnderPPCCloudSite?.find((product) => product.invoiceGroupId)?.invoiceGroupId;
    setInvoiceGroupIdAttachedToExistingSite(existingInvoiceGroupId);

    const productIds = lineItems.map((lineItem) => lineItem.productKey);
    const areSameProductsAlreadyInTheSite =
      productsUnderPPCCloudSite
        .map((product: ExistingProduct) => product.productCatalogId)
        .filter((productId: string) => productIds.includes(productId)).length > 0;

    if (areSameProductsAlreadyInTheSite) {
      errorMessages.push('The same product already exists on this site. Select another site to continue.');
    }
    setAreProductsFound(true);

    if (!noMatchingProductsFound) {
      setExistingCloudSite(finalSiteUrl);
    }

    const allSourceSystems = [...new Set(productsUnderPPCCloudSite?.map((product) => product.sourceSystem))];
    const cloudSiteId = productsUnderPPCCloudSite[0]?.cloudSiteId ?? '';
    const sourceSystem = allSourceSystems?.length > 1 ? SourceSystem.HAMS : allSourceSystems?.[0];
    const cloudSiteUrl = productsUnderPPCCloudSite[0]?.cloudSiteHostname ?? '';
    const siteDetails = {
      siteId: cloudSiteId,
      siteUrl: cloudSiteUrl,
      sourceSystem: sourceSystem,
      products: productsUnderPPCCloudSite.map((product: ExistingProduct) => {
        return {
          productName: product?.productName,
          productCatalogId: product?.productCatalogId,
        };
      }),
      areSameProductsFound: areSameProductsAlreadyInTheSite,
      isNewSite: noMatchingProductsFound,
    };

    const products = lineItems.map((lineItem) => lineItem.productKey);
    const {
      isValid: isExistingSiteValidForProvisioning,
      errorMessage: provisioningErrorMessage,
    } = validateSiteForProvisioning({
      siteDetails,
      products,
    });

    if (!isExistingSiteValidForProvisioning && provisioningErrorMessage) {
      errorMessages.push(provisioningErrorMessage);
    }
    setErrorMessages((prevState) => [...prevState, ...errorMessages]);

    // Fetch Customer contacts for site
    if (errorMessages.length === 0) {
      const entitlementIdsOfCurrentTxa = productsUnderPPCCloudSite.reduce((acc, product) => {
        if (product.transactionAccountId === transactionAccountId) {
          acc.push(product.id);
        }
        return acc;
      }, [] as string[]);
      if (entitlementIdsOfCurrentTxa.length > 0) {
        fetchSiteCustomerContacts(entitlementIdsOfCurrentTxa);
      }
    }

    onExistingSiteAdded({
      siteDetails,
      existingInvoiceGroupId,
      isExistingSiteValidForProvisioning: isExistingSiteValidForProvisioning && !areSameProductsAlreadyInTheSite,
      errorMessage: provisioningErrorMessage,
    });
  }, [
    siteUrl,
    transactionAccountId,
    fetchAllProducts,
    setInvoiceGroupIdAttachedToExistingSite,
    onExistingSiteAdded,
    setExistingCloudSite,
  ]);

  React.useEffect(() => {
    (async () => {
      if (siteUrl !== '') {
        await fetchProducts();
      }
    })();
    return () => {
      setInvoiceGroupIdAttachedToExistingSite(undefined);
      setExistingCloudSite(undefined);
    };
  }, [lineItems.length]);

  return (
    <div className="add-existing-jira-instance-container">
      <Checkbox
        className="add-existing-jira-instance-checkbox"
        testId="add-existing"
        isChecked={isAddingExistingSiteOptionSelected}
        onChange={() => {
          if (isAddingExistingSiteOptionSelected) {
            // User opted out of adding existing site feature. So no existing family container to be used.
            setAreProductsFound(false);
            setErrorMessages([]);
            setFamilyContainerId(undefined);
            setInvoiceGroupIdAttachedToExistingSite(undefined);
            setExistingCloudSite(undefined);
            setSiteUrl('');
          }
          setIsAddingExistingSiteOptionSelected((prevState) => !prevState);
        }}
        label="Add to existing site?"
      />
      {isAddingExistingSiteOptionSelected &&
        (!areProductsFound ? (
          <div className="search-controls-container">
            <Field label="Site" name="existing-site-or-entitlement">
              {({ fieldProps }) => (
                <>
                  <Textfield
                    value={siteUrl}
                    onChange={(e: any) => {
                      fieldProps.onChange(e);
                      setSiteUrl(e.target.value);
                    }}
                    testId="existing-site-or-entitlement"
                    className="txt-existing-site"
                    isCompact={true}
                    placeholder="Enter site"
                  />
                  {isSiteValid === false && <ErrorMessage>Enter a valid site URL</ErrorMessage>}
                </>
              )}
            </Field>
            <LoadingButton
              name="search-existing-jira-instance"
              isLoading={isExistingInstanceBeingSearched}
              className="ppc-search-site-button"
              onClick={async () => {
                if (!checkSiteUrl(finalSiteUrl)) {
                  return;
                }
                await fetchProducts();
              }}
            >
              Search
            </LoadingButton>
          </div>
        ) : (
          <div className="existing-jira-products-container">
            <div className="site-details-actions-container">
              <h1 className="existing-cloud-site-url">
                {finalSiteUrl}
                {errorMessages.length === 0 ? (
                  <EditorSuccessIcon label="cloud-site-found" size="medium" primaryColor={G500} />
                ) : (
                  <EditorErrorIcon label="cloud-site-not-found" size="medium" primaryColor={R500} />
                )}
              </h1>
              <Button
                appearance="link"
                testId="ppc-change-cloud-site-url"
                className="ppc-change-cloud-site-url"
                onClick={() => {
                  setAreProductsFound(false);
                  setExistingCloudSite(undefined);
                  setFamilyContainerId(undefined);
                  setInvoiceGroupIdAttachedToExistingSite(undefined);
                  setErrorMessages([]);
                  onExistingSiteAdded({
                    siteDetails: undefined,
                    existingInvoiceGroupId: undefined,
                    isExistingSiteValidForProvisioning: undefined,
                  });
                  setSiteUrl('');
                }}
                aria-label="Change site URL"
              >
                Remove
              </Button>
            </div>
            {/*{(doesSiteAlreadyHaveTheCurrentProduct || errorMessages.length > 0) && <Divider />}*/}
            <div className="jira-product-names-container">
              {/*{doesSiteAlreadyHaveTheCurrentProduct &&*/}
              {/*  productsUnderPPCCloudSite.map((product) => {*/}
              {/*    return (*/}
              {/*      <>*/}
              {/*        <div className="jira-product-details">*/}
              {/*          <span>*/}
              {/*            <EntitlementIcon productId={product.productCatalogId} className="avatar medium" isCloud />{' '}*/}
              {/*            <label>{product.productName}</label>*/}
              {/*          </span>*/}
              {/*          <h3 className="jira-entitlement">{product.entitlementNumber}</h3>*/}
              {/*        </div>*/}
              {/*      </>*/}
              {/*    );*/}
              {/*  })}*/}
              {errorMessages.map((errorMessage) => {
                return (
                  <SectionMessage title="Not Allowed" appearance="error" key={errorMessage}>
                    <p className="same-products-found-error-message">{renderErrorMessage(errorMessage)}</p>
                  </SectionMessage>
                );
              })}
            </div>
          </div>
        ))}
    </div>
  );
};
